import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Skeleton,
  Typography,
  Result,
  Button,
  Menu,
  Layout,
  Drawer,
  Select,
  Tag,
} from "antd";
import { useHistory, Link } from "react-router-dom";
import { MenuOutlined } from "@ant-design/icons";
import "./MarketplaceSellerDetails.css";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import sellerDetailsQuery from "./queries/sellerDetailsQuery";

// Import tab components
import MarketplaceSellerGeneral from "./tabs/MarketplaceSellerGeneral";
import MarketplaceSellerSettings from "./tabs/MarketplaceSellerSettings";
import MarketplaceSellerCategories from "./tabs/MarketplaceSellerCategories";
import MarketplaceSellerTypologies from "./tabs/MarketplaceSellerTypologies";
import MarketplaceSellerFeatures from "./tabs/MarketplaceSellerFeatures";

const { Title } = Typography;
const { Sider, Content } = Layout;
const { Option } = Select;

const MarketplaceSellerDetails = (props) => {
  const { sellerId } = props.match.params;
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const history = useHistory();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const fetchSeller = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const response = await axios.post(
        graphqlUrl,
        {
          query: sellerDetailsQuery,
          variables: { id: sellerId },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.errors) {
        throw new Error(response.data.errors.map(e => e.message).join(", "));
      }

      setSeller(response.data?.marketplace_seller);
    } catch (error) {
      console.error("Error fetching seller details:", error);
      setError(error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Menu items
  const menuItems = useMemo(
    () =>
      seller
        ? [
            {
              key: "generale",
              label: "Generale",
            },
            {
              key: "configurazioni",
              label: "Configurazioni",
              children: [
                { key: "settings", label: "Impostazioni" },
                { key: "categories", label: "Categorie" },
                { key: "typologies", label: "Tipologie" },
                { key: "features", label: "Features" },
              ],
            },
          ]
        : [],
    [seller]
  );

  // Flatten menu items for mobile dropdown
  const flatMenuItems = useMemo(() => {
    const items = [];
    menuItems.forEach((group) => {
      if (group.children) {
        group.children.forEach((item) => {
          items.push(item);
        });
      } else {
        items.push(group);
      }
    });
    return items;
  }, [menuItems]);

  // Function to check if a tab key is valid
  const isValidTab = useCallback(
    (tabKey) => {
      return flatMenuItems.some((item) => item.key === tabKey);
    },
    [flatMenuItems]
  );

  // Initialize activeTab state
  const initialTab = props.match.params.tabName || "generale";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Find which group contains the active tab and keep it open
  useEffect(() => {
    const activeGroup = menuItems.find((group) => {
      if (group.children) {
        return group.children.some((item) => item.key === activeTab);
      }
      return false;
    });

    if (activeGroup) {
      setOpenKeys([activeGroup.key]);
    }
  }, [activeTab, menuItems]);

  // Redirect to default tab if no tabName is provided
  useEffect(() => {
    if (!props.match.params.tabName) {
      setTimeout(() => {
        history.replace(`/funeralcloud/sellers/${sellerId}/generale`);
      }, 0);
    }
  }, [sellerId, history, props.match.params.tabName]);

  // Update active tab when URL changes
  useEffect(() => {
    if (!seller) return;

    if (props.match.params.tabName && isValidTab(props.match.params.tabName)) {
      setActiveTab(props.match.params.tabName);
    } else {
      setActiveTab("generale");
      if (props.match.params.tabName && !isValidTab(props.match.params.tabName)) {
        setTimeout(() => {
          history.replace(`/funeralcloud/sellers/${sellerId}/generale`);
        }, 0);
      }
    }
  }, [props.match.params.tabName, sellerId, history, seller, isValidTab]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch seller data on mount - only when sellerId actually changes
  useEffect(() => {
    // Only fetch with loading on first mount or when navigating to a different seller
    if (!seller || seller.objectId !== sellerId) {
      fetchSeller(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId]);

  // Handle menu item click
  const handleMenuClick = (e) => {
    setActiveTab(e.key);
    setTimeout(() => {
      history.push(`/funeralcloud/sellers/${sellerId}/${e.key}`);
    }, 0);
    if (isMobile) {
      setDrawerVisible(false);
    }
  };

  // Handle dropdown change for mobile
  const handleDropdownChange = (value) => {
    setActiveTab(value);
    setTimeout(() => {
      history.push(`/funeralcloud/sellers/${sellerId}/${value}`);
    }, 0);
  };

  // Handle submenu open/close
  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  if (loading) {
    return <Skeleton active />;
  }

  if (error) {
    return (
      <div className="marketplace-seller-details-container">
        <Result
          status="500"
          title="Errore Caricamento"
          subTitle={error.message || "Si è verificato un errore durante il caricamento del profilo venditore."}
          extra={
            <Button type="primary" onClick={() => fetchSeller(true)}>
              Riprova
            </Button>
          }
        />
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="marketplace-seller-details-container">
        <Result
          status="404"
          title="Profilo Venditore non trovato"
          subTitle="Il profilo venditore FuneralCloud richiesto non esiste."
          extra={
            <Button type="primary">
              <Link to="/funeralcloud/sellers">Torna alla lista venditori</Link>
            </Button>
          }
        />
      </div>
    );
  }

  // Render the active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "generale":
        return <MarketplaceSellerGeneral seller={seller} onUpdate={fetchSeller} />;
      case "settings":
        return <MarketplaceSellerSettings seller={seller} onUpdate={fetchSeller} />;
      case "categories":
        return <MarketplaceSellerCategories seller={seller} onUpdate={fetchSeller} />;
      case "typologies":
        return <MarketplaceSellerTypologies seller={seller} onUpdate={fetchSeller} />;
      case "features":
        return <MarketplaceSellerFeatures seller={seller} onUpdate={fetchSeller} />;
      default:
        return <MarketplaceSellerGeneral seller={seller} onUpdate={fetchSeller} />;
    }
  };

  return (
    <div className="marketplace-seller-details-container">
      <div className="marketplace-seller-header">
        <div className="marketplace-seller-header-title">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Title level={2} style={{ margin: 0 }}>
              {seller.name}
            </Title>
            <Tag color={seller.enabled ? "green" : "red"} style={{ margin: 0 }}>
              {seller.enabled ? "ATTIVO" : "DISATTIVO"}
            </Tag>
          </div>
        </div>

        {isMobile && (
          <div className="mobile-tab-selector">
            <Select
              value={activeTab}
              onChange={handleDropdownChange}
              style={{ width: "100%", marginBottom: "20px" }}
            >
              {flatMenuItems.map((item) => (
                <Option key={item.key} value={item.key}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </div>
        )}
      </div>

      <Layout className="marketplace-seller-details-layout">
        {!isMobile ? (
          <Sider width={250} className="marketplace-seller-details-sider" theme="light">
            <Menu
              mode="inline"
              selectedKeys={[activeTab]}
              openKeys={openKeys}
              onOpenChange={handleOpenChange}
              onClick={handleMenuClick}
              items={menuItems}
              style={{ height: "100%" }}
            />
          </Sider>
        ) : (
          <div className="mobile-menu-button">
            <Button
              type="primary"
              icon={<MenuOutlined />}
              onClick={() => setDrawerVisible(true)}
              style={{ marginBottom: "16px" }}
            >
              Menu
            </Button>
            <Drawer
              title="Menu"
              placement="left"
              onClose={() => setDrawerVisible(false)}
              open={drawerVisible}
            >
              <Menu
                mode="inline"
                selectedKeys={[activeTab]}
                openKeys={openKeys}
                onOpenChange={handleOpenChange}
                onClick={handleMenuClick}
                items={menuItems}
                style={{ border: "none" }}
              />
            </Drawer>
          </div>
        )}
        <Content className="marketplace-seller-details-content">{renderTabContent()}</Content>
      </Layout>
    </div>
  );
};

export default MarketplaceSellerDetails;
